import { ReactNode } from 'react'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'

interface HoverableCellWrapperProps {
  children: ReactNode
  onCellHover: (hovered: boolean) => void
  onCellDoubleClick?: () => void
}

/**
 * Hoverable Cell Wrapper
 *
 * Makes the entire table cell (including empty space between content) respond to hover events.
 * Useful when hovering the empty space in a cell should trigger an action (e.g. previewing the first item),
 * while still allowing interactive elements inside to work normally.
 *
 * The wrapper renders two layers:
 * 1. Hover detection layer: absolutely positioned over the full cell; captures hover events anywhere inside.
 * 2. Content layer: displays the actual cell content but has `pointer-events-none` so events pass through to the hover layer.
 *
 * Requirements:
 * - Parent `<td>` must have `position: relative` - handled automatically for columns with `meta.hoverable = true` in `MedChronEventsTable` and `MedSummaryEntriesTable`.
 * - Any interactive elements in children must explicitly use `pointer-events-auto` so they remain clickable or hoverable on their own.
 * */
export default function HoverableCellWrapper({ children, onCellHover, onCellDoubleClick }: HoverableCellWrapperProps) {
  const isTouchOnlyDevice = useIsTouchOnlyDevice()

  return (
    <>
      {/* Layer 1: Hover detection layer – covers full cell */}
      <div
        className="absolute inset-0 z-10"
        // Ignore hover events on touch devices
        {...(!isTouchOnlyDevice && {
          onMouseEnter: () => onCellHover(true),
          onMouseLeave: () => onCellHover(false),
        })}
        {...(onCellDoubleClick && { onDoubleClick: onCellDoubleClick })}
        aria-hidden
      />

      {/* Layer 2: Content layer – visually on top, but non-interactive by default */}
      <div className="relative z-20 w-full h-full pointer-events-none">{children}</div>
    </>
  )
}
